import { listReservations } from '@/modules/reservations/services/reservationRepository';
import type { Reservation } from '@/modules/reservations/types';
import { todayIsoDate } from '@/modules/reservations/utils/dateRange';
import {
  findOverlappingBlockingReservations,
  isBlockingReservation,
} from '@/modules/reservations/utils/reservationFilters';

export type ReservationDashboardStats = {
  activeReservations: number;
  upcomingReservations: number;
  itemsCurrentlyAffected: number;
  detectedConflicts: number;
};

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isUpcoming(reservation: Reservation, today: string, withinDays: number): boolean {
  const until = addDays(today, withinDays);
  return (
    isBlockingReservation(reservation) &&
    reservation.startDate > today &&
    reservation.startDate <= until
  );
}

function isActiveNow(reservation: Reservation, today: string): boolean {
  return (
    isBlockingReservation(reservation) &&
    reservation.startDate <= today &&
    reservation.endDate >= today
  );
}

/** Detecta pares de reservas bloqueantes que comparten ítem y fechas solapadas. */
function countDataConflicts(reservations: Reservation[]): number {
  const blocking = reservations.filter(isBlockingReservation);
  let conflicts = 0;

  for (let i = 0; i < blocking.length; i++) {
    const a = blocking[i];
    if (!a) continue;
    for (let j = i + 1; j < blocking.length; j++) {
      const b = blocking[j];
      if (!b) continue;
      const sharedItems = a.inventoryItemIds.filter((id) =>
        b.inventoryItemIds.includes(id),
      );
      if (sharedItems.length === 0) continue;
      const overlaps = findOverlappingBlockingReservations(
        [b],
        a.inventoryItemIds,
        a.startDate,
        a.endDate,
      );
      if (overlaps.length > 0) conflicts += 1;
    }
  }

  return conflicts;
}

export async function fetchReservationDashboardStats(): Promise<ReservationDashboardStats> {
  const reservations = await listReservations();
  const today = todayIsoDate();

  const active = reservations.filter((r) => isActiveNow(r, today));
  const upcoming = reservations.filter((r) => isUpcoming(r, today, 14));

  const affectedIds = new Set<string>();
  for (const r of active) {
    for (const id of r.inventoryItemIds) affectedIds.add(id);
  }

  return {
    activeReservations: active.length,
    upcomingReservations: upcoming.length,
    itemsCurrentlyAffected: affectedIds.size,
    detectedConflicts: countDataConflicts(reservations),
  };
}
